import '../styles/LandingEntryPanel.css';
import React, { useEffect, useRef, useState } from 'react';

const LIST_FINAL = 128;
const COUNT_DURATION = 900;

const SIGNAL_LABELS = {
  idle: 'Ready to sign in',
  navigating: 'Opening Login',
  failed: 'Login route unavailable',
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);
  return reduced;
}

function useCountUp(target, duration, reduced) {
  const [value, setValue] = useState(reduced ? target : 0);
  const frame = useRef(0);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return undefined;
    }
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) frame.current = window.requestAnimationFrame(step);
    };
    frame.current = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame.current);
  }, [target, duration, reduced]);

  return value;
}

function LandingLineArt() {
  return (
    <div className="lep-art" role="img" aria-label="Diagram of a profile card connecting to a graph of related profiles">
      <div className="lep-art-head">
        <p className="lep-micro">Profile → connection graph</p>
        <p className="lep-micro">Illustrative structure</p>
      </div>
      <svg className="lep-art-svg" viewBox="0 0 640 190" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
        {/* profile cards */}
        <rect x="8" y="18" width="150" height="64" rx="4" className="lep-stroke" />
        <circle cx="34" cy="50" r="11" className="lep-stroke" />
        <line x1="54" y1="42" x2="126" y2="42" className="lep-stroke" />
        <line x1="54" y1="52" x2="104" y2="52" className="lep-stroke" />
        <line x1="54" y1="62" x2="84" y2="62" className="lep-stroke" />
        <text x="54" y="96" aria-hidden="true" />

        <rect x="8" y="96" width="150" height="64" rx="4" className="lep-stroke" />
        <circle cx="34" cy="128" r="11" className="lep-stroke" />
        <line x1="54" y1="120" x2="132" y2="120" className="lep-stroke" />
        <line x1="54" y1="130" x2="98" y2="130" className="lep-stroke" />
        <line x1="54" y1="140" x2="76" y2="140" className="lep-stroke" />

        <rect x="178" y="50" width="150" height="64" rx="4" className="lep-stroke" />
        <circle cx="204" cy="82" r="11" className="lep-stroke" />
        <line x1="224" y1="74" x2="306" y2="74" className="lep-stroke" />
        <line x1="224" y1="84" x2="270" y2="84" className="lep-stroke" />
        <line x1="224" y1="94" x2="244" y2="94" className="lep-stroke" />

        {/* connection graph */}
        <line x1="158" y1="50" x2="236" y2="82" className="lep-stroke" />
        <line x1="158" y1="128" x2="236" y2="82" className="lep-stroke-rule" />

        <line x1="328" y1="82" x2="392" y2="52" className="lep-stroke" />
        <line x1="328" y1="82" x2="392" y2="112" className="lep-stroke" />
        <line x1="392" y1="52" x2="452" y2="34" className="lep-stroke-rule" />
        <line x1="392" y1="52" x2="452" y2="74" className="lep-stroke-rule" />
        <line x1="392" y1="112" x2="452" y2="92" className="lep-stroke-rule" />
        <line x1="392" y1="112" x2="452" y2="132" className="lep-stroke-rule" />
        <line x1="452" y1="34" x2="536" y2="34" className="lep-stroke-rule" />
        <line x1="452" y1="74" x2="536" y2="74" className="lep-stroke-rule" />
        <line x1="452" y1="92" x2="536" y2="92" className="lep-stroke-rule" />
        <line x1="452" y1="132" x2="536" y2="132" className="lep-stroke-rule" />

        <circle cx="392" cy="52" r="5" className="lep-node" />
        <circle cx="392" cy="112" r="5" className="lep-node" />
        <circle cx="452" cy="34" r="3" className="lep-node" />
        <circle cx="452" cy="74" r="3" className="lep-node" />
        <circle cx="452" cy="92" r="3" className="lep-node" />
        <circle cx="452" cy="132" r="3" className="lep-node" />

        {/* result rows */}
        <circle cx="548" cy="34" r="4" className="lep-node" />
        <line x1="560" y1="34" x2="626" y2="34" className="lep-stroke-rule" />
        <circle cx="548" cy="74" r="4" className="lep-signal-node" />
        <line x1="560" y1="74" x2="626" y2="74" className="lep-stroke" />
        <circle cx="548" cy="92" r="4" className="lep-node" />
        <line x1="560" y1="92" x2="620" y2="92" className="lep-stroke-rule" />
        <circle cx="548" cy="132" r="4" className="lep-node" />
        <line x1="560" y1="132" x2="624" y2="132" className="lep-stroke-rule" />

        {/* baseline rules */}
        <line x1="0" y1="176" x2="640" y2="176" className="lep-stroke-rule" />
        <line x1="0" y1="188" x2="640" y2="188" className="lep-stroke-rule" />
      </svg>
    </div>
  );
}

function LandingEntryPanel({ onGoToLogin }) {
  const reduced = useReducedMotion();
  const count = useCountUp(LIST_FINAL, COUNT_DURATION, reduced);
  const [navState, setNavState] = useState('idle');

  const goToLogin = async () => {
    if (typeof onGoToLogin === 'function') {
      setNavState('navigating');
      try {
        await onGoToLogin();
        setNavState('idle');
      } catch (err) {
        setNavState('failed');
      }
      return;
    }
    setNavState('failed');
  };

  return (
    <div className="lep-root">
      <div className="lep-rail" data-content-rail="primary">
        <div className="lep-panel">
          <div className="lep-col lep-col--head">
            <div className="lep-head">
              <span className="lep-wordmark">
                <span className="lep-wordmark-mark" aria-hidden="true">
                  <svg viewBox="0 0 18 18" focusable="false">
                    <rect x="0.5" y="0.5" width="17" height="17" rx="3" fill="none" stroke="currentColor" strokeWidth="1" />
                    <line x1="4" y1="6" x2="14" y2="6" stroke="currentColor" strokeWidth="1" />
                    <line x1="4" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="1" />
                    <line x1="4" y1="12" x2="13" y2="12" stroke="currentColor" strokeWidth="1" />
                  </svg>
                </span>
                leads-linkedin
              </span>
              <h1 className="lep-headline">Extracts leads from LinkedIn.</h1>
              <p className="lep-explain">
                Each extraction returns a <strong>lead list</strong> you can review now and return to in a later
                session. You start the run; the instrument returns the profiles.
              </p>
            </div>
          </div>

          <div className="lep-col lep-col--counter" aria-label="Illustrative extraction counter">
            <div className="lep-readout">
              <span className="lep-dot" aria-hidden="true" />
              <div>
                <p className="lep-count" aria-live="off">
                  {count}
                  <span className="lep-count-suffix" aria-hidden="true">/list</span>
                </p>
                <span className="lep-visually-hidden">Example list total: 128 leads per extraction</span>
              </div>
            </div>
            <div className="lep-readout-meta">
              <p className="lep-micro">Sample result total</p>
              <p className="lep-readout-title">Leads returned per extraction</p>
              <div className="lep-scale" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="lep-scale-caption" aria-hidden="true">
                <span>Illustrative</span>
                <span>Not account data</span>
              </div>
            </div>
            <p className="lep-note">
              Shown as an example of the readout. Your own totals appear after a run.
            </p>
          </div>

          <div className="lep-col lep-col--signal">
            <p className="lep-micro">Entry</p>
            <button type="button" className="lep-signal-btn" onClick={goToLogin}>
              <span>{navState === 'navigating' ? 'Opening Login…' : 'Enroll or sign in'}</span>
              <span className="lep-signal-arrow" aria-hidden="true">
                <svg viewBox="0 0 18 18" focusable="false">
                  <line x1="2" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="1.5" />
                  <polyline points="10,4 15,9 10,14" fill="none" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </span>
            </button>

            <p className="lep-signal-state" data-state={navState}>
              <i aria-hidden="true" />
              {SIGNAL_LABELS[navState]}
            </p>

            {navState === 'failed' && (
              <div>
                <p className="lep-failure" role="alert">
                  {typeof onGoToLogin === 'function'
                    ? 'Navigation to Login did not complete. Nothing was lost.'
                    : 'Navigation to Login is not available in this preview.'}
                </p>
                <button type="button" className="lep-retry" onClick={goToLogin}>
                  Retry entry
                </button>
              </div>
            )}

            <dl className="lep-spec">
              <dt>Access</dt>
              <dd>Sign-in required</dd>
              <dt>Source</dt>
              <dd>LinkedIn profiles</dd>
              <dt>Saved</dt>
              <dd>Leads page</dd>
            </dl>
          </div>

          <LandingLineArt />
        </div>
      </div>
    </div>
  );
}

export default LandingEntryPanel;